'use client';

import { useState } from 'react';
import { AlertCircle, ChevronRight, RefreshCw, Square, TriangleAlert } from 'lucide-react';
import { Header } from '@/components/header';
import { MetricsTimeRangeControls } from '@/components/metrics-time-range-controls';
import { Sidebar } from '@/components/sidebar';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { cn } from '@/lib/utils';
import { METRICS_TIME_RANGES, type MetricsTimeRangeKey } from '@/lib/metrics-time-range';
import { AnalysisProgress } from './components/AnalysisProgress';
import { AnalysisOverview } from './components/AnalysisOverview';
import { EventExplorer } from './components/EventExplorer';
import { RateLimitReplayView } from './components/RateLimitReplayView';
import { WorkloadCostView } from './components/WorkloadCostView';
import { useInterferenceAnalysis } from './hooks/useInterferenceAnalysis';
import { groupEventsByIncident } from './logic';

type AnalysisTab = 'cost' | 'events' | 'replay';

export default function InterferenceAnalysisPage() {
  const [range, setRange] = useState(() => {
    const toMs = Date.now();
    const preset = METRICS_TIME_RANGES.find((item) => item.key === '6h')!;
    return { fromMs: toMs - preset.durationMs, toMs, presetLabel: preset.label };
  });
  const [tab, setTab] = useState<AnalysisTab>('events');
  const analysis = useInterferenceAnalysis(range.fromMs, range.toMs);
  const incidentCount = analysis.result ? groupEventsByIncident(analysis.result.events).length : 0;

  const setPreset = (key: MetricsTimeRangeKey) => {
    const preset = METRICS_TIME_RANGES.find((item) => item.key === key)!;
    const toMs = Date.now();
    setRange({ fromMs: toMs - preset.durationMs, toMs, presetLabel: preset.label });
  };

  return (
    <div className="flex h-screen">
      <Sidebar />
      <div className="flex min-w-0 flex-1 flex-col overflow-hidden">
        <Header />
        <main className="flex-1 overflow-auto px-4 pb-12 pt-4 lg:px-6">
          <div className="mx-auto max-w-[1500px] space-y-4">
            <div className="flex flex-wrap items-start justify-between gap-3">
              <div>
                <div className="flex flex-wrap items-center gap-2">
                  <h1 className="text-2xl font-bold">API interference analysis</h1>
                  <Badge variant="outline" className="border-amber-400 bg-amber-50 text-amber-800 dark:border-amber-800 dark:bg-amber-950/40 dark:text-amber-200">Experimental</Badge>
                </div>
                <p className="mt-1 max-w-3xl text-sm text-muted-foreground">
                  Find periods where one user&apos;s traffic rose before another user slowed down, then test whether weighted limits would have reacted.
                </p>
              </div>
              <div className="flex flex-wrap items-center justify-end gap-2">
                <MetricsTimeRangeControls
                  fromMs={range.fromMs}
                  toMs={range.toMs}
                  onSetFromTo={(fromMs, toMs) => setRange({ fromMs, toMs, presetLabel: '' })}
                  onSetPreset={setPreset}
                  activePresetLabel={range.presetLabel}
                  onSetNow={() => {
                    const duration = range.toMs - range.fromMs;
                    const toMs = Date.now();
                    setRange((current) => ({ ...current, fromMs: toMs - duration, toMs }));
                  }}
                />
                {analysis.active && (
                  <Button variant="outline" onClick={analysis.cancel}>
                    <Square className="h-3.5 w-3.5 fill-current" />
                    Cancel
                  </Button>
                )}
                <Button variant="outline" onClick={() => void analysis.refetch()} disabled={analysis.active}>
                  <RefreshCw className={cn('h-4 w-4', analysis.active && 'animate-spin')} />
                  Run analysis
                </Button>
              </div>
            </div>

            {(analysis.active || analysis.progress.workloadPages > 0 || analysis.progress.phase === 'cancelled') && <AnalysisProgress progress={analysis.progress} />}

            {analysis.progress.phase === 'idle' && !analysis.result && (
              <div className="rounded-lg border bg-muted/20 p-8 text-center">
                <h2 className="font-semibold">Ready to analyze</h2>
                <p className="mt-1 text-sm text-muted-foreground">Choose a time range, then click Run analysis. No metrics are fetched automatically.</p>
              </div>
            )}

            {analysis.error && (
              <div role="alert" className="flex items-start justify-between gap-3 rounded-lg border border-destructive/50 bg-destructive/10 px-4 py-3 text-sm text-destructive">
                <span className="flex gap-2"><AlertCircle className="mt-0.5 h-4 w-4 shrink-0" /><span><strong>Analysis could not be completed.</strong><br />{analysis.error}</span></span>
                <Button variant="outline" size="sm" onClick={() => void analysis.refetch()}>Retry</Button>
              </div>
            )}

            {analysis.empty && !analysis.loading && (
              <div className="rounded-lg border bg-muted/20 p-10 text-center">
                <h2 className="font-semibold">No workload metrics in this range</h2>
                <p className="mt-1 text-sm text-muted-foreground">Choose a different time range and run the analysis again.</p>
              </div>
            )}

            {analysis.result && (
              <>
                <AnalysisOverview analysis={analysis.result} incidentCount={incidentCount} />

                <div className="flex flex-wrap items-center justify-between gap-3 rounded-lg border bg-card px-4 py-3 shadow-sm">
                  <p className="text-sm"><strong>{analysis.result.workload_interval_count.toLocaleString()}</strong> workload intervals · <strong>{analysis.result.ratelimit_entry_count.toLocaleString()}</strong> rate-limit samples</p>
                  <p className="text-xs text-muted-foreground">{analysis.result.latency_statistic === 'mean' ? 'Mean latency fallback' : 'p95 latency'}{analysis.loadedAt ? ` · Completed ${new Date(analysis.loadedAt).toLocaleTimeString()}` : ''}</p>
                </div>

                {analysis.result.warnings.length > 0 && (
                  <details className="group rounded-lg border border-amber-300 bg-amber-50 px-4 py-3 text-sm text-amber-950 dark:border-amber-900 dark:bg-amber-950/30 dark:text-amber-100">
                    <summary className="flex cursor-pointer list-none items-center gap-2 font-semibold"><ChevronRight className="h-4 w-4 transition-transform group-open:rotate-90" /><TriangleAlert className="h-4 w-4" />Data quality details ({analysis.result.warnings.length})</summary>
                    <ul className="mt-2 list-disc space-y-1 pl-5">{analysis.result.warnings.map((warning) => <li key={warning}>{warning}</li>)}</ul>
                  </details>
                )}

                <div role="tablist" aria-label="Interference analysis views" className="flex w-fit max-w-full overflow-x-auto rounded-lg border bg-card p-1">
                  <Tab active={tab === 'events'} onClick={() => setTab('events')}>Incident evidence</Tab>
                  <Tab active={tab === 'cost'} onClick={() => setTab('cost')}>Workload weights</Tab>
                  <Tab active={tab === 'replay'} onClick={() => setTab('replay')}>Limiter simulation</Tab>
                </div>

                {tab === 'cost' && <WorkloadCostView costs={analysis.result.workload_costs} workloadKeys={analysis.result.workload_keys} />}
                {tab === 'events' && <EventExplorer events={analysis.result.events} />}
                {tab === 'replay' && (
                  <RateLimitReplayView
                    key={`${range.fromMs}-${range.toMs}-${analysis.loadedAt ?? 0}`}
                    analysis={analysis.result}
                    recalculating={analysis.recalculating}
                    onRecalculate={analysis.recalculate}
                  />
                )}
              </>
            )}
          </div>
        </main>
      </div>
    </div>
  );
}

function Tab({ active, onClick, children }: { active: boolean; onClick: () => void; children: React.ReactNode }) {
  return <button type="button" role="tab" aria-selected={active} onClick={onClick} className={cn('whitespace-nowrap rounded-md px-4 py-2 text-sm font-medium', active ? 'bg-primary text-primary-foreground' : 'text-muted-foreground hover:bg-muted')}>{children}</button>;
}
